import { Box, Button, Paper, TextField, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { toast } from 'material-react-toastify';
import { ValidatedCoupon } from '../../interfaces/Coupon/ValidatedCoupon';
import { PaymentForm } from '../../interfaces/Checkout/PaymentForm';
import { isTokenValid } from '../../utils/Auth';
import { getValidatedCoupon } from '../../api/Coupon';
import { useThemeColor } from '../../hooks/useThemeColor';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { useLocalizedText } from '../../hooks/useLocalizedText';

type CouponCardProps = {
  form: PaymentForm;
  onUseCoupon: (coupon: ValidatedCoupon) => void;
  hasOffer: boolean;
};

export const CouponCard = ({
  form,
  onUseCoupon,
  hasOffer,
}: CouponCardProps) => {
  const t = useLocalizedText();

  const { primary, secondary } = useThemeColor();

  const [code, setCode] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleUseCoupon = useCallback(() => {
    setShowConfirmDialog(false);

    getValidatedCoupon(code)
      .then((x) => {
        toast.success(t('checkout.coupon_card.success'));
        onUseCoupon(x);
      })
      .catch((err) => {
        toast.error(err.message);
      });
  }, [code]);

  const handleSubmit = useCallback(() => {
    if (hasOffer) {
      setShowConfirmDialog(true);
      return;
    }

    handleUseCoupon();
  }, [handleUseCoupon, hasOffer]);

  return isTokenValid() ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 1.5,
        alignItems: 'stretch',
      }}
    >
      <ConfirmDialog
        content={t('checkout.coupon_card.offer_will_be_removed_warning')}
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleUseCoupon}
        color='primary'
      />

      <Paper
        elevation={3}
        sx={{
          px: 2.5,
          pt: 1,
          pb: 2,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>{t('checkout.coupon')}</Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <TextField
            disabled={!!form.couponAmount}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            size='small'
            fullWidth
          />

          <Button
            disabled={!!form.couponAmount || !code.length}
            variant='contained'
            onClick={handleSubmit}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              color: secondary,
              backgroundColor: primary,
            }}
          >
            {t('common.confirm')}
          </Button>
        </Box>
      </Paper>
    </Box>
  ) : null;
};
